import React, { useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  isSameMonth,
  isSameDay,
  format,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { phaseForDate, PHASE_ORDER } from "../lib/cycle";
import { useI18n } from "../i18n";

export default function CycleCalendar({ data }) {
  const { phases, weekdays, dateLocale, t } = useI18n();
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const today = new Date();

  const gridStart = startOfWeek(startOfMonth(month));
  const gridEnd = endOfWeek(endOfMonth(month));
  const days = [];
  let cur = gridStart;
  while (cur <= gridEnd) {
    days.push(cur);
    cur = addDays(cur, 1);
  }

  return (
    <div
      data-testid="cycle-calendar"
      className="bg-bone border border-border rounded-3xl p-5 sm:p-6 animate-fade-up"
    >
      <div className="flex items-center justify-between mb-5">
        <button
          data-testid="cal-prev-month"
          onClick={() => setMonth(addMonths(month, -1))}
          className="h-9 w-9 grid place-items-center rounded-full border border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <h3 className="font-serif text-2xl text-foreground" data-testid="cal-month-label">
          {format(month, "MMMM yyyy", { locale: dateLocale })}
        </h3>
        <button
          data-testid="cal-next-month"
          onClick={() => setMonth(addMonths(month, 1))}
          className="h-9 w-9 grid place-items-center rounded-full border border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map((d, i) => (
          <div
            key={`${d}-${i}`}
            className="text-center text-[10px] tracking-[0.15em] font-bold text-muted-foreground py-1"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {days.map((day) => {
          const inMonth = isSameMonth(day, month);
          const { phaseKey, cycleDay } = phaseForDate(day, data);
          const phase = phases[phaseKey];
          const isToday = isSameDay(day, today);
          return (
            <div
              key={day.toISOString()}
              data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
              className="aspect-square rounded-xl flex flex-col items-center justify-center relative"
              style={{
                backgroundColor: inMonth ? phase.color : "transparent",
                color: inMonth ? phase.textColor : "#B8B2A6",
                opacity: inMonth ? 1 : 0.35,
                outline: isToday ? "2.5px solid #2D2A26" : "none",
                outlineOffset: isToday ? "2px" : "0",
              }}
              title={inMonth ? `${phase.label} · ${cycleDay}` : ""}
            >
              <span className={`text-sm ${isToday ? "font-extrabold" : "font-medium"}`}>
                {format(day, "d")}
              </span>
              {isToday && inMonth && (
                <span className="absolute -bottom-0.5 text-[7px] font-bold tracking-wider uppercase">
                  {t("today")}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-5 border-t border-border grid grid-cols-2 gap-y-2.5 gap-x-3">
        {PHASE_ORDER.map((k) => {
          const p = phases[k];
          return (
            <div key={k} className="flex items-center gap-2">
              <span
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: p.color }}
              />
              <span className="text-xs text-text-secondary text-muted-foreground truncate">
                {p.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
